<script setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageComponent from '@/components/common/ImageComponent.vue';
import PreviewHeader from '@/components/preview/kakao/common/PreviewHeader.vue';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';
import ConsentInformation from './ConsentInformation.vue';

const props = defineProps({
  brandWideItemList: {
    type: Object,
    default: () => ({}),
  },
  position: {
    type: String,
    default: '',
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
});

const {
  brandWideItemList,
  position,
} = toRefs(props);
const { t } = useI18n();

const mainItem = computed(() => {
  const p = position.value;
  const title = brandWideItemList.value.header || t('kakaoBasic.preview.title');
  const content = ((brandWideItemList.value?.list || [])?.[0]?.content || t('kakaoBasic.preview.content'));
  const list = [content.split('\n')[0], content.split('\n').slice(1).join('')];

  return {
    title,
    contentList: list.map((v) => v || '\u00A0'),
    itemClass: p === 'wide-item-list-0' ? 'editAreaPositionHover' : '',
    titleClass: p === 'wide-item-list-header' && title ? 'editAreaPositionHover' : '',
    contentClass: p === 'wide-item-list-0-content' && content.length ? 'editAreaPositionHover' : '',
    imageClass: p === 'wide-item-list-0-image' ? 'editAreaPositionHover' : '',
    imageUrl: (brandWideItemList.value?.list || [])?.[0]?.image?.url,
  };
});

const displayWideItemList = computed(() => (brandWideItemList.value?.list || [])
  .filter((_, i) => (i !== 0))
  .map((item, i) => {
    const p = position.value;
    const content = (item.content || t('kakaoBasic.preview.content'));
    const list = [content.split('\n')[0], content.split('\n').slice(1).join('')];

    return {
      itemClass: p === `wide-item-list-${i + 1}` ? 'editAreaPositionHover' : '',
      contentClass: p === `wide-item-list-${i + 1}-content` && content.length ? 'editAreaPositionHover' : '',
      imageClass: p === `wide-item-list-${i + 1}-image` ? 'editAreaPositionHover' : '',
      contentList: list.map((v) => v || '\u00A0'),
      imageUrl: item.image?.url,
    };
  }));

const buttonsClass = computed(() => (position.value === 'wide-item-list-buttons' ? 'editAreaPositionHover' : ''));
const couponClass = computed(() => {
  if (!brandWideItemList.value?.coupon?.enabled) {
    return 'd-none';
  }

  return position.value === 'wide-item-list-coupon' ? 'editAreaPositionHover' : '';
});

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const couponTitle = computed(() => {
  const title = brandWideItemList.value.coupon?.name;
  const type = brandWideItemList.value.coupon?.type;
  const str = t(couponType[type] ?? 'empty');
  const value = type === 'DELIVERY_DISCOUNT' ? str : `${title}${str.slice(2)}`;

  return value;
});

const couponDescription = computed(() => brandWideItemList.value.coupon?.description || '');

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <div class="preview-talk preview-talk--active">
    <ConsentInformation />
    <div class="preview-inner mockup-scroll">
      <PreviewHeader
        :kakao-profile="kakaoProfile"
      />
      <div class="preview-message wide wideItemList preview-message--active">
        <div>
          <h5
            class="wideItemList__subject"
            :class="mainItem.titleClass"
          >
            {{ mainItem.title }}
          </h5>
          <div
            class="wideItemList__img--wrap"
            :class="mainItem.itemClass"
          >
            <div
              class="wideItemList__img"
              :class="mainItem.imageClass"
            >
              <ImageComponent
                :src="mainItem.imageUrl"
                alt="widItem"
              />
            </div>
            <div
              class="wideItemList__desc"
              :class="mainItem.contentClass"
            >
              <p
                v-for="(elm, i) in mainItem.contentList"
                :key="`wideItemList-preview-main-item-title-${i}`"
              >{{ elm }}</p>
            </div>
          </div>
        </div>
        <div class="preview-message__inner">
          <ul class="wideItemList__list">
            <li
              v-for="(item, i) in displayWideItemList"
              :key="`wideItemList-preview-item-${i}`"
              class="wideItemList__item"
              :class="item.itemClass"
            >
              <div
                class="item__image"
                :class="item.imageClass"
              >
                <ImageComponent
                  :src="item.imageUrl"
                  alt="widItem"
                />
              </div>
              <div
                class="item__desc"
                :class="item.contentClass"
              >
                <p
                  v-for="(elm, j) in item.contentList"
                  :key="`wideItemList-preview-item-${i}-title-${j}`"
                >{{ elm }}</p>
              </div>
            </li>
          </ul>
        </div>
        <div
          class="preview-message__button preview-message__button--horizontal"
          :class="buttonsClass"
        >
          <button
            v-for="(item, index) in brandWideItemList.buttons"
            :key="`wideItemList-preview-button${index}`"
            type="button"
            :class="{
              'preview-message__button--kakao': item.type === 'AC',
              'preview-message__button--yellow': item.type === 'BF',
            }"
          >
            {{ item.name }}
          </button>
        </div>
        <div class="preview-message__coupon">
          <button
            type="button"
            :class="couponClass"
          >
            <div class="coupon__text">
              <h5 class="coupon__title">{{ couponTitle }}</h5>
              <p class="coupon__desc">{{ couponDescription }}</p>
            </div>
            <div class="coupon__icon">
              <i></i>
              <span>COUPON</span>
            </div>
          </button>
        </div>
        <PreviewFooter />
      </div>
    </div>
  </div>
</template>
<style>
</style>
